import type { ActionPayload, UIElement } from '../../common/types.js';
import { isSafeUrl, safeSetAttribute } from '../../common/safe-html.js';
import { addImageErrorHandler } from '../../common/product-utils.js';
import type { ChatUISpecRenderContext } from '../types.js';

type PreviewProduct = {
  imageUrl: string | undefined;
  name: string | undefined;
};

export const FRONTEND_PANEL_RESTORE_ACTION = 'frontend_restore_panel_message';

function asString(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function asPreviewProducts(value: unknown): PreviewProduct[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const rec = entry as Record<string, unknown>;
      return {
        imageUrl: asString(rec['imageUrl']) || undefined,
        name: asString(rec['name']) || undefined,
      };
    })
    .filter((entry): entry is PreviewProduct => entry !== null);
}

export function renderPanelRestoreCard(element: UIElement, ctx: ChatUISpecRenderContext): HTMLElement {
  const props = (element.props ?? {}) as Record<string, unknown>;
  const eyebrow = asString(props['eyebrow']);
  const title = asString(props['title']);
  const showTitle = props['showTitle'] !== false;
  const ctaLabel = asString(props['ctaLabel']) || ctx.i18n?.showPanelAriaLabel || 'Show Results';
  const panelMessageId = asString(props['panelMessageId']);
  const highlightMessageId = asString(props['highlightMessageId']);
  const threadId = asString(props['threadId']);
  const products = asPreviewProducts(props['products']).slice(0, 3);

  const card = document.createElement('button');
  card.type = 'button';
  card.className = 'gengage-chat-panel-restore-card gds-card';
  if (!eyebrow && !showTitle) {
    card.classList.add('gengage-chat-panel-restore-card--minimal');
  }
  card.dataset['gengagePart'] = 'panel-restore-card';
  const ariaLabelSource = title || eyebrow || ctaLabel;
  card.setAttribute('aria-label', title ? `${ariaLabelSource} - ${ctaLabel}` : ariaLabelSource);

  const action: ActionPayload = {
    title,
    type: FRONTEND_PANEL_RESTORE_ACTION,
    payload: {
      panelMessageId,
      highlightMessageId,
      threadId,
    },
  };
  card.addEventListener('click', () => ctx.onAction(action));

  if (eyebrow) {
    const eyebrowEl = document.createElement('div');
    eyebrowEl.className = 'gengage-chat-panel-restore-card__eyebrow';
    eyebrowEl.textContent = eyebrow;
    card.appendChild(eyebrowEl);
  }

  if (products.length > 0) {
    const previewsEl = document.createElement('div');
    previewsEl.className = 'gengage-chat-panel-restore-card__previews';
    for (const product of products) {
      const thumb = document.createElement('div');
      thumb.className = 'gengage-chat-panel-restore-card__thumb';
      const imageUrl = product.imageUrl;
      if (imageUrl && isSafeUrl(imageUrl)) {
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.alt = product.name || title;
        safeSetAttribute(img, 'src', imageUrl);
        addImageErrorHandler(img);
        thumb.appendChild(img);
      }
      previewsEl.appendChild(thumb);
    }
    card.appendChild(previewsEl);
  }

  if (showTitle && title) {
    const titleEl = document.createElement('div');
    titleEl.className = 'gengage-chat-panel-restore-card__title';
    titleEl.textContent = title;
    titleEl.title = title;
    card.appendChild(titleEl);
  }

  const ctaEl = document.createElement('div');
  ctaEl.className = 'gengage-chat-panel-restore-card__cta';
  ctaEl.textContent = ctaLabel;
  card.appendChild(ctaEl);

  return card;
}
